import { AlertDTO } from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import { LoadingModal } from 'components/modal';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxButton, FmlxLabel, FmlxModal, FmlxTextBox } from 'fmlx-common-ui';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { showAlertSnackBar, MaxNameLength } from 'utils/Global';
interface RenameComponentProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  treeNodeName: string;
  treeNodeId: number;
  refreshTreeNodeAfterRename: ({ id, newName }: TreeNodeRenameParams) => void;
}

type TreeNodeRenameParams = {
  id: number;
  newName: string;
};

const TEXT_BOX_ID = 'textbox-rename-item';

export default function RenameComponent(props: RenameComponentProps) {
  const {
    showModal,
    setShowModal,
    treeNodeName,
    treeNodeId,
    refreshTreeNodeAfterRename,
  } = props;
  const [newName, setNewName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textBoxContainerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (showModal) {
      setNewName(treeNodeName);
    }
  }, [showModal]);

  const renameNode = useCallback(async () => {
    const nodeNewName = newName.trim();

    let alertMessage = null;
    if (nodeNewName.length > MaxNameLength.TREE_NODE) {
      alertMessage = `The name ${nodeNewName} is too large. Please choose another name not greater than ${MaxNameLength.TREE_NODE} characters.`;
    }

    if (!alertMessage) {
      setShowModal(false);
      setIsLoading(true);

      const treeService = new TreeService();
      const response: AlertDTO = await treeService.renameNode(treeNodeId, nodeNewName);
      setIsLoading(false);
      if (response.alertType == EAlertType.ERROR) {
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.alertMessage);
      } else {
        refreshTreeNodeAfterRename({
          id: treeNodeId,
          newName: nodeNewName,
        });
      }
    } else {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, alertMessage);
    }
  }, [newName, treeNodeId, refreshTreeNodeAfterRename]);

  const setFocus = () => {
    const divElement = textBoxContainerRef.current;
    if (!divElement) return;
    const inputs = divElement.getElementsByTagName('input');
    if (inputs.length === 0) return;
    const input = inputs[0];
    input?.focus();
    input?.click();
  };

  const handleInputChange = useCallback((value) => {
    setNewName(value);
  }, []);

  useLayoutEffect(() => {
    if (showModal) {
      // use timeout to make sure
      // text input component is mounted
      setTimeout(() => {
        setFocus();
      }, 100);
    }
  }, [showModal]);

  return (
    <>
      <LoadingModal showModal={isLoading} text={'Renaming'} />
      {showModal && (
        <FmlxModal
          id='commandpanel-renamecomponent-modal'
          open
          title="Rename Item"
          onCloseClick={() => setShowModal(false)}
          primaryButton={{
            label: 'OK',
            disabled: newName.trim() === '',
            onClick: () => renameNode(),
            show: true,
            variant: FmlxButton.Variant.CONTAIN,
            type: FmlxButton.Type.PRIMARY,
          }}
          secondaryButton={{ show: false } as any}
          tertiaryButton={{
            label: 'Cancel',
            disabled: false,
            onClick: () => setShowModal(false),
            show: true,
            variant: FmlxButton.Variant.OUTLINE,
            type: FmlxButton.Type.PRIMARY,
          }}
        >
          <div ref={textBoxContainerRef} id="commandpanel-renamecomponent-input-wrapper" className="mb-3">
            <FmlxLabel id="commandpanel-renamecomponent-label" label="Enter New Name:" />
            <FmlxTextBox
              id={`commandpanel-renamecomponent-${TEXT_BOX_ID}`}
              value={newName}
              onChange={(arg) => handleInputChange(arg.value)}
              maxLength={MaxNameLength.TREE_NODE}
              variant={FmlxTextBox.Variant.STANDARD}
            />
          </div>
        </FmlxModal>
      )}
    </>
  );
}
